import {
  DEFAULT_DENIED_REDIRECT,
  ROUTE_ACCESS_RULES,
  STUDENT_FALLBACK_RULE,
} from './routeAccess.config';
import type { MyTxtPermission } from './permissions.types';
import type { RouteAccessRule } from './routeAccess.types';
import {
  MY_TXT_SESSION_USER_TYPES,
  type MyTxtSessionUserType,
} from './myTxtUserTypes';

export function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function matchRuleForPath(pathname: string): RouteAccessRule {
  const path = normalizePathname(pathname);
  const explicit = ROUTE_ACCESS_RULES.filter(
    r => r.pathPrefix.length > 0,
  ).sort((a, b) => b.pathPrefix.length - a.pathPrefix.length);
  const matched = explicit.find(
    rule =>
      path === rule.pathPrefix || path.startsWith(`${rule.pathPrefix}/`),
  );
  return matched ?? STUDENT_FALLBACK_RULE;
}

export function canUserAccessPath(
  userType: string | undefined | null,
  pathname: string,
): boolean {
  if (!userType) {
    return false;
  }
  const rule = matchRuleForPath(pathname);
  return (rule.allowedUserTypes as readonly string[]).includes(userType);
}

export function getDeniedRedirect(rule: RouteAccessRule): string {
  return rule.deniedRedirect ?? DEFAULT_DENIED_REDIRECT;
}

export function getRuleById(ruleId: string): RouteAccessRule {
  const rule = ROUTE_ACCESS_RULES.find(r => r.id === ruleId);
  if (!rule) {
    throw new Error(`Unknown route access rule: ${ruleId}`);
  }
  return rule;
}

export function userMayAccessRule(
  userType: string | undefined | null,
  rule: RouteAccessRule,
): boolean {
  if (!userType) {
    return false;
  }
  return (rule.allowedUserTypes as readonly string[]).includes(userType);
}

export function isMyTxtSessionUserType(
  value: string,
): value is MyTxtSessionUserType {
  return (MY_TXT_SESSION_USER_TYPES as readonly string[]).includes(value);
}

export type NavTreeNodeLike = {
  to?: string;
  children?: NavTreeNodeLike[];
};

export function filterNavTreeForUser<T extends NavTreeNodeLike>(
  userType: string | null | undefined,
  nodes: T[],
): T[] {
  if (!userType) {
    return [];
  }
  return nodes
    .map(node => {
      if (node.children?.length) {
        const children = filterNavTreeForUser(userType, node.children);
        if (children.length === 0) {
          return null;
        }
        return { ...node, children };
      }
      if (node.to && !canUserAccessPath(userType, node.to)) {
        return null;
      }
      return node;
    })
    .filter((n): n is T => n !== null);
}

export function canAccessRule(
  rule: RouteAccessRule,
  can: (permission: MyTxtPermission) => boolean,
): boolean {
  if (rule.publicAccess) {
    return true;
  }
  if (rule.anyOfReadPermissions?.length) {
    return rule.anyOfReadPermissions.some(permission => can(permission));
  }
  return can(rule.readPermission);
}

export function filterNavTreeByPermissions<T extends NavTreeNodeLike>(
  nodes: T[],
  can: (permission: MyTxtPermission) => boolean,
): T[] {
  return nodes
    .map(node => {
      if (node.children?.length) {
        const children = filterNavTreeByPermissions(node.children, can);
        if (children.length === 0) {
          return null;
        }
        return { ...node, children };
      }
      if (node.to) {
        const rule = matchRuleForPath(node.to);
        if (!canAccessRule(rule, can)) {
          return null;
        }
      }
      return node;
    })
    .filter((n): n is T => n !== null);
}
